import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
  type GetCompanyProfileResponse,
  getCompanyProfileQueryKey,
  getLogo,
  getLogoQueryKey,
  uploadLogoMutation,
} from "@/api";
import { LOGO_MAX_BYTES, LOGO_TYPES } from "./companyForm";

export type LogoProblem = "type" | "size";

/** Client-side check mirroring the backend (PNG/JPEG/SVG/WebP, ≤ 5 MB). */
export function checkLogo(file: File): LogoProblem | null {
  if (!LOGO_TYPES.includes(file.type)) return "type";
  if (file.size > LOGO_MAX_BYTES) return "size";
  return null;
}

/**
 * The logo is an authenticated GET returning image bytes, so it's fetched as a
 * blob through the generated client and shown via an object URL.
 */
export function useCompanyLogoUrl(enabled: boolean): string | undefined {
  const logo = useQuery({
    queryKey: getLogoQueryKey(),
    queryFn: async ({ signal }) => {
      const { data } = await getLogo({ parseAs: "blob", signal, throwOnError: true });
      return data as Blob;
    },
    enabled,
    retry: false,
  });
  const url = useMemo(() => (logo.data ? URL.createObjectURL(logo.data) : undefined), [logo.data]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  return url;
}

export function useUploadLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    ...uploadLogoMutation(),
    // Patch logo_url in the cached profile rather than refetching it: a refetch
    // bumps updated_at, which remounts the form and would drop unsaved edits.
    onSuccess: (result) => {
      queryClient.setQueryData<GetCompanyProfileResponse>(getCompanyProfileQueryKey(), (old) =>
        old?.data ? { ...old, data: { ...old.data, logo_url: String(result.data.logo_url ?? "") } } : old,
      );
      return queryClient.invalidateQueries({ queryKey: getLogoQueryKey() });
    },
  });
}
